import { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../../components/ui/sheet';
import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';
import { AlertTriangle, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../lib/api';

const inr = n => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;
const when = v => (v ? new Date(v).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
const OUT = [['accountable', 'Trainer accountable'], ['not_fault', "Not trainer's fault"], ['sent_back', 'Sent back to trainer']];
const Row = ({ k, v }) => <div className="grid grid-cols-[130px_1fr] gap-2 py-1 text-xs border-b border-slate-50"><span className="text-slate-400">{k}</span><span className="text-slate-800 whitespace-pre-wrap break-words">{v || '—'}</span></div>;

const ConfirmTrainer = ({ c, trainers, onDone }) => {
  const [tid, setTid] = useState(c.trainer_id || '');
  const [reason, setReason] = useState('');
  const go = async () => {
    try { await api.post(`/cn-review/cases/${c.creditnote_id}/confirm-trainer`, { trainer_id: tid, reason }); toast.success('Trainer confirmed, flag removed'); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="rounded-md border border-rose-200 bg-rose-50/60 p-3 space-y-2" data-testid="cn-confirm-trainer">
      {c.flags.map(x => <p key={x} className="text-xs text-rose-800 flex gap-1.5"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{x}</p>)}
      <p className="text-[11px] text-slate-600">Confirm the trainer this credit note belongs to. It will count in that trainer's report and review.</p>
      <select value={tid} onChange={e => setTid(e.target.value)} className="text-xs border border-slate-200 rounded-md px-2 h-8 bg-white w-full" data-testid="cn-confirm-trainer-select">
        <option value="">Pick trainer…</option>{trainers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</select>
      <Textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason for removing the flag (required)" className="text-xs min-h-[60px] bg-white" data-testid="cn-confirm-reason" />
      <Button size="sm" onClick={go} disabled={!tid || reason.trim().length < 5} data-testid="cn-confirm-btn"><ShieldCheck className="w-3.5 h-3.5 mr-1.5" />Confirm trainer & remove flag</Button>
    </div>
  );
};

const Review = ({ c, onDone }) => {
  const [o, setO] = useState('');
  const [note, setNote] = useState('');
  const go = async () => {
    try { await api.post(`/cn-review/cases/${c.creditnote_id}/review`, { outcome: o, note }); toast.success('Review saved'); setO(''); setNote(''); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="rounded-md border border-slate-200 p-3 space-y-2" data-testid="cn-review-form">
      <p className="text-xs font-semibold text-slate-800">Manager review — why did this happen?</p>
      <div className="flex flex-wrap gap-1.5">{OUT.map(([k, l]) => (
        <button key={k} onClick={() => setO(k)} data-testid={`cn-outcome-${k}`} className={`text-xs px-2.5 py-1 rounded-full border ${o === k ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 text-slate-600'}`}>{l}</button>))}</div>
      <Textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Review note (required)" className="text-xs min-h-[70px]" data-testid="cn-review-note" />
      <Button size="sm" onClick={go} disabled={!o || note.trim().length < 5} data-testid="cn-review-submit">Save review</Button>
    </div>
  );
};

export const CnCaseDrawer = ({ c, trainers, onClose, onChanged }) => {
  const [, tick] = useState(0);
  useEffect(() => tick(x => x + 1), [c]);
  const done = () => onChanged();
  return (
    <Sheet open={!!c} onOpenChange={v => !v && onClose()}>
      <SheetContent className="w-[560px] sm:max-w-[560px] overflow-y-auto" data-testid="cn-case-drawer">
        {c && <>
          <SheetHeader><SheetTitle className="font-mono">{c.creditnote_number}</SheetTitle></SheetHeader>
          <p className="text-xs text-slate-500 mb-3">{c.date} · {inr(c.total)} · {c.cn_status} · created by {c.created_by_name}</p>
          <div className="mb-4">
            <Row k="Student" v={`${c.customer_name}${c.student_name && c.student_name !== c.customer_name ? ` (${c.student_name})` : ''}`} />
            <Row k="Enrolment No" v={c.enrollment_no} /><Row k="Course" v={c.course || c.items?.join(', ')} />
            <Row k="Credit Note Type" v={c.cn_type} /><Row k="Trainer on CN" v={c.cn_trainer_name} /><Row k="Linked trainer" v={c.trainer_name} />
            <Row k="Batch(es)" v={c.batches?.map(b => `${b.batch_number || ''} ${b.name} — ${b.trainer_name}`).join('\n')} />
            <Row k="Refund reason" v={c.refund_reason} /><Row k="Invoices" v={c.invoices?.join(', ')} />
            {c.lead_id && <Row k="CRM lead" v={<a className="text-blue-700 hover:underline" target="_blank" rel="noreferrer" href={`https://crm.zoho.com/crm/tab/Leads/${c.lead_id}`}>Open lead</a>} />}
          </div>
          {c.is_training && <div className="space-y-3">
            {c.flags?.length > 0 && <ConfirmTrainer c={c} trainers={trainers} onDone={done} />}
            {c.flag_cleared && <p className="text-[11px] text-emerald-700 bg-emerald-50 rounded px-2 py-1" data-testid="cn-flag-cleared">Flag removed by {c.flag_cleared.by}: {c.flag_cleared.reason}</p>}
            {c.stage === 'awaiting_review' && <Review c={c} onDone={done} />}
            {c.stage === 'awaiting_trainer' && <p className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1.5">Waiting for {c.trainer_name || 'the trainer'} to respond.</p>}
          </div>}
          <h4 className="text-xs font-bold text-slate-800 mt-5 mb-2">Timeline</h4>
          <ol className="space-y-2 border-l border-slate-200 pl-3" data-testid="cn-timeline">
            {(c.timeline || []).slice().reverse().map((e, i) => (
              <li key={i} className="text-xs"><div className="text-slate-800 whitespace-pre-wrap">{e.text}</div>
                <div className="text-[10px] text-slate-400">{e.by}{e.role ? ` (${e.role})` : ''} · {when(e.at)}</div></li>))}
          </ol>
        </>}
      </SheetContent>
    </Sheet>
  );
};
